
import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { View, RefreshControl, FlatList, Pressable, useWindowDimensions, StyleSheet, Platform } from 'react-native';
import { SmallCard, Text, Title, ScrollView } from '@constants/Themed';
import { useTranslation } from 'react-i18next';
import { FontAwesome, Entypo, SimpleLineIcons, FontAwesome5, MaterialIcons, Ionicons } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import DefaultScreen from '@components/Global/DefaultScreenView';
import apis from '@api/api';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState, store } from '@reduxLocal/persistState';
import { useQuery } from '@tanstack/react-query';
import styles from '@stylesheet';
import { useDataFoldersQuery, createPaginationState, updatePaginationState, resetPaginationPage } from '@utils/dataQueries';
import { Pagination } from '@components/Global/Pagination/Pagination';
import LottieView from '@utils/lottieAnimation';
import { filter, uniqueId, debounce } from 'lodash';
import DataOptionsModal, { DataOptionsModalHandle } from '@components/modals/DataOptionsModal/DataOptionsModal';
import ShareUserModal, { ShareConfirmHandle } from '@components/modals/ShareUserModal/ShareUserModal';
import ActionsModal, { ActionModalHandle } from '@components/modals/ActionsModal/ActionsModal';
import RenderDataItems from '../components/DataItem/DataItem';
import RenderInvoice from '../components/DataItem/InvoiceItem';
import GlobalTutorial from '@components/TutorialComponent/GlobalTutorial';
import { dataCarouselData } from '../../ProjectManagementScreen/Tutorial/CarouselData';
import DataDeleteConfirmModal, { DeleteConfirmModalHandle } from '@components/modals/DeleteConfirmModal/DataDeleteConfirmModal';
import QuickAddDataModal, { QuickAddHandle } from '@components/modals/QuickAddDataModal/QuickAddDataModal';
import QuickSearchModal from '@components/modals/QuickSearchModal/QuickSearchModal';
import LinkGenModal from './Components/LinkGenModal';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
// import SwipeableRobot, { RobotHandle } from '@components/RobotHelper/SwipeableRobot';
import { HOST } from '@api/constant';
import { useDeviceType } from '@hooks/useDeviceType';

// Removed Virtuoso imports - using FlatList instead
import { XStack, YStack, Card as TamaguiCard } from 'tamagui';
import HomeQuickStats from '../../WelcomeScreen/Widgets/Components/SubComponents/GaugeStats';
import { filters } from '../../../../NavigationStacks/DataStack';
import CloseButton from '@components/UI/CloseButton';
import DocumentItem from '../components/DataItem/DocumentItem';
import { fetchTemplatesByFilter } from './utils/templateHelpers';
import toTitleCase from '@utils/toTitleCase';
import { useIsFreePlanWithContext, useCurrentPlan } from '@utils/billingUtils';
import useGlobalUser from '@hooks/getGlobalUser';

const MAX_ITEM_WIDTH = 450;
const MAX_CONTAINER_WIDTH = 1280; // Max width constraint for the page

const CaptureScreen = ({ navigation }) => {
  const { t } = useTranslation();
  const [refreshing, setRefreshing] = useState(false);
  const [width, setWidth] = useState(0);
  const dataFilter = useSelector((state: DefaultRootState) => state.UI.dataFilter);
  const [searchState, setSearchState] = useState('');
  const [listHorizontal, setListHorizontal] = useState(false);
  const [activeItem, setActiveItem] = useState({});
  const shareUserRef = useRef<ShareConfirmHandle>(null);
  const [dismissedCards, setDismissedCards] = useState<{ [key: string]: boolean }>({});
  const [newCarouselData, setNewCarouselData] = useState(dataCarouselData);
  const dataOptionsRef = useRef<DataOptionsModalHandle>(null);
  const actionsRef = useRef<ActionModalHandle>(null);
  const deleteConfirmRef = useRef<DeleteConfirmModalHandle>(null); // Corrected type
  const quickAddRef = useRef<QuickAddHandle>(null);
  const linkGenRef = useRef(null);
  const searchRef = useRef(null);
  const [columns, setColumns] = useState(Math.max(1, Math.floor(width / (MAX_ITEM_WIDTH + 20))));
  const dispatch = useDispatch();


  // Initialize containerWidth to width (like ProjectManagementScreen)
  const [containerWidth, setContainerWidth] = useState(width);

  const robotRef = useRef<any>(null);
  const selectedId = useGlobalUser();
  const menuResizing = useSelector((state: any) => state.UI.menuTransitioning);
  const theme = themeSelector();
  const { isTablet } = useDeviceType();
  const [databaseConnections, setDatabaseConnections] = useState([]);
  const [selectedDatabaseConnection, setSelectedDatabaseConnection] = useState<string | null>(null);

  // Header collapsible state (internal to this component)
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);

  // Pagination state using utils
  const [paginationState, setPaginationState] = useState(() =>
    createPaginationState({ currentPage: 1, pageSize: 12 })
  );

  const textHelp = useMemo(() => [ // Memoize textHelp
    'Think of the items here as your data folders, you can manage them as you like',
    'Managing and sharing your data has never been easier',
    'I can understand what your data is about and help you manage it',
    'Depending on your plan, you can add more data to your account',
    'Your data will change according to the type of data identified, and you can manage it accordingly',
  ], []);

  // Use paginated query for data folders (or sent invoices if invoice filter)
  // Note: Backend uses companyNumber from session, so even if selectedId is user ID,
  // the backend will return company data when companyNumber exists in session
  const { data: paginatedData, isLoading, refetch } = useDataFoldersQuery({
    selectedId: selectedId,
    dataFilter: dataFilter === 'invoices' ? undefined : dataFilter, // Don't use dataFolders query for invoices
    pagination: paginationState,
    enabled: !!selectedId && dataFilter !== 'invoices' // Disable for invoices, use separate query
  });

  // Separate query for sent invoices
  const { data: sentInvoicesData, isLoading: sentInvoicesLoading, refetch: refetchSentInvoices } = useQuery({
    queryKey: ['sent-invoices', selectedId, paginationState.currentPage, paginationState.pageSize, paginationState.sortBy, paginationState.sortOrder],
    queryFn: async () => {
      const response = await apis.getSentInvoices({
        page: paginationState.currentPage,
        limit: paginationState.pageSize,
        sortBy: paginationState.sortBy === 'updatedAt' ? 'issue_date' : paginationState.sortBy,
        sortOrder: paginationState.sortOrder
      });
      return response;
    },
    enabled: !!selectedId && dataFilter === 'invoices',
    retry: 2,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Load database connections
  const loadDatabaseConnections = useCallback(async () => {
    try {
      const connections = await apis.getDatabaseConnections(undefined);
      setDatabaseConnections(connections || []);
    } catch (error) {
      console.error('Error loading database connections:', error);
    }
  }, []);

  // Refetch when selectedId changes (e.g., after company switch)
  // This ensures we get fresh data from backend which uses session companyNumber
  useEffect(() => {
    if (selectedId) {
      if (dataFilter === 'invoices') {
        refetchSentInvoices();
      } else {
        refetch();
      }
      // Load database connections
      loadDatabaseConnections();
    }
  }, [selectedId, refetch, refetchSentInvoices, dataFilter, loadDatabaseConnections]);

  // Fallback query for templates and non-paginated data
  const { data: fallbackData, isLoading: fallbackLoading } = useQuery({
    queryKey: ['dataFolders-fallback', dataFilter, selectedId],
    queryFn: async () => {
      // Handle template queries for documents and invoices
      if (dataFilter === 'documents') {
        const templates = await fetchTemplatesByFilter('documents');
        if (!templates?.length) {
          setNewCarouselData([{ title: 'Hey, we tried...', caption: 'Looks like you may not have any document templates yet, why not create some?', image: <LottieView loop style={{ width: '100%', flex: 1 }} source={require('@assets/animations/magnifExample.json')} /> }, ...dataCarouselData]);
          return [];
        }
        return templates;
      }
      
      if (dataFilter === 'invoices') {
        // Fetch sent invoices instead of templates
        const response = await apis.getSentInvoices({
          page: 1,
          limit: 100, // Get all for fallback
          sortBy: 'issue_date',
          sortOrder: 'desc'
        });
        const invoices = response?.data?.records || [];
        if (!invoices?.length) {
          setNewCarouselData([{ title: 'Hey, we tried...', caption: 'Looks like you may not have any sent invoices yet. Create and send an invoice to see it here.', image: <LottieView loop style={{ width: '100%', flex: 1 }} source={require('@assets/animations/magnifExample.json')} /> }, ...dataCarouselData]);
          return [];
        }
        return invoices;
      }
      
      // Default data folder query for other filters
      const data = await apis.getDataFolders({ userId: selectedId, dataFilter: dataFilter || "all" });
      if (!data?.length) {
        setNewCarouselData([{ title: 'Hey, we tried...', caption: 'Looks like you may not have any models yet, why not make some?', image: <LottieView loop style={{ width: '100%', flex: 1 }} source={require('@assets/animations/magnifExample.json')} /> }, ...dataCarouselData]);
        return [];
      }
      return data;
    },
    enabled: !paginatedData && (dataFilter === 'documents' || dataFilter === 'invoices'),
    retry: 2,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60 * 5,
  });

  // Use paginated data if available, otherwise fallback
  // For invoices, use sent invoices data
  const dataFolderList = dataFilter === 'invoices'
    ? (sentInvoicesData?.data?.records || fallbackData || [])
    : (paginatedData?.records || fallbackData || []);

  // Check plan status for unlock logic
  const isFreePlan = useIsFreePlanWithContext();
  const currentPlan = useCurrentPlan();
  const isPlanExpired = currentPlan?.status === 'expired' || currentPlan?.status === 'cancelled';
  const dataFolderCount = dataFolderList?.length || 0;
  const shouldShowUnlock = useMemo(() => {
    // Show unlock if: (free plan AND more than 3 folders) OR plan is expired
    return (isFreePlan && dataFolderCount > 3) || isPlanExpired;
  }, [isFreePlan, dataFolderCount, isPlanExpired]);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setPaginationState(prev => updatePaginationState(prev, { currentPage: page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPaginationState(prev => resetPaginationPage(updatePaginationState(prev, { pageSize })));
  }, []);

  const handleSortByChange = useCallback((sortBy: string) => {
    setPaginationState(prev => resetPaginationPage(updatePaginationState(prev, { sortBy })));
  }, []);

  const handleSortOrderChange = useCallback((sortOrder: 'asc' | 'desc') => {
    setPaginationState(prev => resetPaginationPage(updatePaginationState(prev, { sortOrder })));
  }, []);

  const processedData = useMemo(() => {
    if (!dataFolderList?.length) return [];
    
    let filteredData = dataFolderList;
    
    // Apply filter based on dataFilter
    if (dataFilter && dataFilter !== 'all') {
      // For documents and invoices, data is already filtered by the query
      if (dataFilter === 'documents' || dataFilter === 'invoices') {
        // Template data is already filtered, just use as-is
        filteredData = dataFolderList;
      } else {
        // Apply tag-based filtering for other data types
        const getFilterTags = () => {
          if (dataFilter.toLowerCase() === 'customers') {
            return ['customer', 'customers', 'Customer', 'Customers'];
          } else if (dataFilter.toLowerCase() === 'products') {
            return ['product', 'products', 'Product', 'Products'];
          } else if (dataFilter.toLowerCase() === 'inventory') {
            return ['inventory', 'Inventory', 'stock', 'Stock'];
          } else {
            return [];
          }
        };
        
        const filterTags = getFilterTags();
        if (filterTags.length > 0) {
          filteredData = dataFolderList.filter(item => 
            filterTags.some(tag => (item.dataTags ?? []).includes(tag))
          );
        }
      }
    }
    
    // Apply search filter
    if (!searchState) return filteredData;

    const formattedQuery = searchState.toLowerCase().replace(/ /g, '_');
    
    // Handle search for templates vs regular data
    if (dataFilter === 'documents') {
      return filteredData.filter(item =>
        (item.title ?? '').toLowerCase().includes(searchState.toLowerCase()) ||
        (item.template_name ?? '').toLowerCase().includes(searchState.toLowerCase()) ||
        (item.templateTitle ?? '').toLowerCase().includes(searchState.toLowerCase())
      );
    } else if (dataFilter === 'invoices') {
      // Search sent invoices by invoice number, client name, status
      return filteredData.filter(item =>
        (item.invoice_number ?? '').toLowerCase().includes(searchState.toLowerCase()) ||
        (item.client?.name ?? '').toLowerCase().includes(searchState.toLowerCase()) ||
        (item.client?.email ?? '').toLowerCase().includes(searchState.toLowerCase()) ||
        (item.status ?? '').toLowerCase().includes(searchState.toLowerCase()) ||
        (item.title ?? '').toLowerCase().includes(searchState.toLowerCase())
      );
    } else {
      // Regular data folder search
      return filteredData.filter(item =>
        (item.dataTitle ?? '').toLowerCase().includes(formattedQuery) || (item.dataTags ?? []).includes(formattedQuery)
      );
    }
  }, [searchState, dataFolderList, dataFilter]);

  // Handle container layout changes - only update containerWidth, let useEffect handle columns
  const handleContainerLayout = useCallback((event) => {
    const { width: measuredWidth } = event.nativeEvent.layout;
    if (measuredWidth > 0 && measuredWidth !== containerWidth) {
      setContainerWidth(measuredWidth);
    }
  }, [containerWidth]);

  const calculateColumns = useCallback((parentWidth) => {
    // Calculate number of columns based on available width and item width (400px)
    // Add some padding/margin considerations (e.g., 40px container padding + 20px gap between items)
    const containerPadding = 40; // 20px on each side
    const itemGap = 20; // Gap between items
    const availableWidth = parentWidth - containerPadding;

    const calculatedColumns = Math.floor((availableWidth + itemGap) / (MAX_ITEM_WIDTH + itemGap));
    // Always ensure at least 1 column, maximum reasonable columns for screen size
    const finalColumns = Math.max(1, Math.min(calculatedColumns, 6));

    return finalColumns;
  }, [width]);
  // Update columns when window width or container width changes
  useEffect(() => {
    const debounceResize = debounce(() => {
      const parentWidth = containerWidth || width;
      const calculatedColumns = calculateColumns(parentWidth);

      if (parentWidth > 1600 || isTablet) {
        setColumns(calculatedColumns);
        setListHorizontal(false);
      } else {
        // For smaller screens, use single column
        setListHorizontal(false);
        setColumns(Math.max(1, calculatedColumns));
      }
    }, 100); // Reduced debounce time for faster response

    debounceResize();

    return () => {
      debounceResize.cancel();
    };
  }, [width]);

  const handleGenLink = useCallback((item) => linkGenRef.current?.openModal?.(item), []);
  const handleQuickAdd = useCallback((item) => quickAddRef.current?.openModal(item), []);
  const handleShareModal = useCallback((item) => { setActiveItem(item); shareUserRef.current?.openModal(item); }, []);
  const handleSettingsModal = useCallback((item) => { setActiveItem(item); dataOptionsRef.current?.openModal(item); }, []);
  const handleDeleteModal = useCallback((item) => deleteConfirmRef.current?.openModal(item), []);
  const handleActionModal = useCallback((item) => actionsRef.current?.openModal(item), []);
  const handleSearch = useCallback((item) => searchRef.current?.openModal(item), []);

  // Calculate column width: (containerWidth - padding - gaps) / columns
  // columnWrapperStyle has paddingHorizontal: 20 = 20px left + 20px right = 40px total
  const columnWidth = useMemo(() => {
    if (listHorizontal || columns <= 1) return MAX_ITEM_WIDTH;
    const padding = 40; // paddingHorizontal: 20 means 20px on each side = 40px total
    const gap = 20;
    const constrainedWidth = Math.min(containerWidth || width, MAX_CONTAINER_WIDTH);
    const availableWidth = constrainedWidth - padding;
    const totalGaps = gap * (columns - 1);
    return Math.floor((availableWidth - totalGaps) / columns);
  }, [containerWidth, width, columns, listHorizontal]);

  // Render item - use width: 100% to fill column, let FlatList handle gaps via columnWrapperStyle
  const renderVirtuosoItem = useCallback(({ item, index }) => {
    if (!item) return null;

    if (dataFilter === 'documents') {
      return (
        <View style={{ flex: 1 }} key={item._id || index}>
          <DocumentItem
            availableWidth={columnWidth}
            handleGenLink={handleGenLink}
            handleQuickAdd={handleQuickAdd}
            handleShareModal={handleShareModal}
            handleSettingsModal={handleSettingsModal}
            handleDeleteModal={handleDeleteModal}
            handleActionModal={handleActionModal}
            handleRefresh={refetch}
            item={item}
            index={index}
            listHorizontal={listHorizontal}
            handleSearch={handleSearch}
            handleShareState={undefined}
          />
        </View>
      );
    }
    if (dataFilter === 'invoices') {
      return (
        <View style={{ flex: 1 }} key={item._id || index}>
          <RenderInvoice
            availableWidth={columnWidth}
            handleGenLink={handleGenLink}
            handleQuickAdd={handleQuickAdd}
            handleShareModal={handleShareModal}
            handleSettingsModal={handleSettingsModal}
            handleDeleteModal={handleDeleteModal}
            handleActionModal={handleActionModal}
            handleRefresh={dataFilter === 'invoices' ? refetchSentInvoices : refetch}
            item={item}
            index={index}
            listHorizontal={listHorizontal}
            handleSearch={handleSearch}
            handleShareState={undefined}
          />
        </View>
      );
    }
    return (
      <View style={{ flex: 1 }} key={item._id || index}>
        <RenderDataItems
          availableWidth={columnWidth}
          handleGenLink={handleGenLink}
          handleQuickAdd={handleQuickAdd}
          handleShareModal={handleShareModal}
          handleSettingsModal={handleSettingsModal}
          handleDeleteModal={handleDeleteModal}
          handleActionModal={handleActionModal}
          handleRefresh={refetch}
          item={item}
          index={index}
          listHorizontal={listHorizontal}
          handleSearch={handleSearch}
          handleShareState={undefined}
          shouldShowUnlock={shouldShowUnlock}
        />
      </View>
    );
  }, [handleGenLink, handleQuickAdd, handleShareModal, handleSettingsModal, handleDeleteModal, handleActionModal, refetch, listHorizontal, handleSearch, dataFilter, columnWidth]);



  const handleCreateAction = useCallback(() => {
    if (dataFilter === 'documents') {
      navigation.navigate('DocumentTemplates');
    } else if (dataFilter === 'invoices') {
      navigation.navigate('InvoiceTemplates');
    } else {
      navigation.navigate('CreateData');
    }
  }, [dataFilter, navigation]);

  const ListEmptyComponent = useMemo(() => () => (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 30, gap: 30 }}>
      {searchState ? (
        <View style={[styles.noteContainer, { padding: 20, alignItems: 'center' }]}>
          <LottieView source={require('@assets/animations/88006-warning-yellow.json')} autoPlay loop={false} style={{ width: '80%', height: 200 }} />
          <Text>
            {dataFilter === 'documents' 
              ? 'No document templates found matching your search.' 
              : dataFilter === 'invoices' 
              ? 'No invoice templates found matching your search.'
              : 'No items found matching your query.'}
          </Text>
        </View>
      ) : (
          <View style={{
            borderRadius: 20,
            padding: 40,
            marginVertical: 16,
            backgroundColor: !theme ? '#1e293b' : '#ffffff',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: !theme ? 0.4 : 0.12,
            shadowRadius: 32,
            elevation: !theme ? 10 : 6,
            borderWidth: 1,
            borderColor: !theme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
            overflow: 'hidden',
            position: 'relative',
            alignItems: 'center',
            justifyContent: 'center',
            borderStyle: 'dashed',
            alignSelf: 'center',
            width: '100%',
            maxWidth: 600
          }}>
            <Animatable.View animation="pulse" iterationCount="infinite" duration={3000}>
              {dataFilter === 'documents' ? (
                <Ionicons name="document-text" size={48} color={!theme ? 'rgba(102, 126, 234, 0.5)' : 'rgba(74, 144, 226, 0.5)'} />
              ) : dataFilter === 'invoices' ? (
                <FontAwesome5 name="file-invoice-dollar" size={48} color={!theme ? 'rgba(102, 126, 234, 0.5)' : 'rgba(74, 144, 226, 0.5)'} />
              ) : (
                <Entypo name="folder" size={48} color={!theme ? 'rgba(102, 126, 234, 0.5)' : 'rgba(74, 144, 226, 0.5)'} />
              )}
            </Animatable.View>
            <Text style={{ marginTop: 16, color: !theme ? '#94a3b8' : '#64748b', fontSize: 18, fontWeight: '600' }}>
              {dataFilter === 'documents' ? 'Create your first document' :
                dataFilter === 'invoices' ? 'Create your first invoice' :
                  'Create your first data collection'}
            </Text>
            <Text style={{ marginTop: 8, color: !theme ? '#64748b' : '#94a3b8', textAlign: 'center' }}>
              {dataFilter === 'documents' ? 'Get started by creating a new document template.' :
                dataFilter === 'invoices' ? 'Get started by creating a new invoice template.' :
                  'Get started by creating a new data collection to organize your information.'}
            </Text>
            <Pressable
              onPress={handleCreateAction}
              style={{
                marginTop: 20,
                backgroundColor: !theme ? '#667eea' : '#4a90e2',
                borderRadius: 20,
                paddingVertical: 10,
                paddingHorizontal: 20,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8
              }}
            >
              <MaterialIcons name="create-new-folder" size={18} color="white" />
              <Text style={{ color: 'white', fontWeight: '600' }}>
                {dataFilter === 'documents' ? 'Create Document' :
                  dataFilter === 'invoices' ? 'Create Invoice' :
                    'Create Collection'}
              </Text>
            </Pressable>
        </View>
      )}
    </View>
  ), [searchState, newCarouselData, dataFilter, theme, handleCreateAction]);

  // Header component with stats from DataCounter
  const HeaderComponent = useCallback(() => {
    // Get total counts from pagination data or fallback to current page data
    const totalDataItems = paginatedData?.data?.pagination?.totalRecords || processedData?.length || 0;
    const totalRecords = processedData.reduce((acc, item) => acc + (item?.count || 0), 0);
    const activeFilter = filters.find(f => f.value === dataFilter);

    return (
      <Animatable.View
        animation="fadeInDown"
        duration={800}
        style={[
          styles.modernCard,
          theme ? styles.modernBgLight : styles.modernBgDark,
          {
            marginVertical: 16,
            marginHorizontal: 20,
          }
        ]}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: isHeaderCollapsed ? 0 : 20 }}>
          <View style={{
            backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
            borderRadius: 12,
            padding: 12,
            marginRight: 16
          }}>
            <MaterialIcons
              name="storage"
              size={28}
              color={theme ? "#0ea5e9" : "#38bdf8"}
            />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={[
              styles.modernCardTitle,
              {
                color: theme ? "#1e293b" : "#f1f5f9",
                fontSize: 24,
                fontWeight: '700',
                marginBottom: 4
              }
            ]}>
              Data Store
            </Text>
            {!isHeaderCollapsed && (
              <Text style={{
                color: theme ? "#64748b" : "#94a3b8",
                fontSize: 14
              }}>
                Manage and organize your data collections
              </Text>
            )}
          </View>
          <Pressable
            onPress={() => setIsHeaderCollapsed(!isHeaderCollapsed)}
            style={{
              padding: 8,
              borderRadius: 8,
              backgroundColor: theme ? "#f1f5f9" : "#1e293b"
            }}
          >
            <MaterialIcons
              name={isHeaderCollapsed ? "expand-more" : "expand-less"}
              size={24}
              color={theme ? "#64748b" : "#94a3b8"}
            />
          </Pressable>
        </View>

        {/* Collapsible Content */}
        {!isHeaderCollapsed && (
          <XStack flexWrap='wrap' gap={8}>
            {/* Stats Grid */}
            <YStack flex={1} gap={8}>
              {/* Database Connection Filter */}
              {databaseConnections.length > 0 && (
                <View style={{
                  marginBottom: 12,
                  padding: 12,
                  backgroundColor: theme ? "#f8fafc" : "#1e293b",
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: theme ? "#e2e8f0" : "#334155"
                }}>
                  <Text style={{
                    color: theme ? "#1e293b" : "#f1f5f9",
                    fontSize: 14,
                    fontWeight: '600',
                    marginBottom: 8
                  }}>
                    Database Connections
                  </Text>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                    <Pressable
                      onPress={() => setSelectedDatabaseConnection(null)}
                      style={{
                        paddingHorizontal: 12,
                        paddingVertical: 6,
                        borderRadius: 8,
                        backgroundColor: !selectedDatabaseConnection
                          ? (theme ? "#3b82f6" : "#2563eb")
                          : (theme ? "#e2e8f0" : "#334155"),
                        borderWidth: 1,
                        borderColor: !selectedDatabaseConnection
                          ? (theme ? "#2563eb" : "#1d4ed8")
                          : (theme ? "#cbd5e1" : "#475569")
                      }}
                    >
                      <Text style={{
                        color: !selectedDatabaseConnection
                          ? "white"
                          : (theme ? "#64748b" : "#94a3b8"),
                        fontSize: 12,
                        fontWeight: '600'
                      }}>
                        All Data
                      </Text>
                    </Pressable>
                    {databaseConnections.map((conn) => (
                      <Pressable
                        key={conn._id}
                        onPress={() => setSelectedDatabaseConnection(conn._id)}
                        style={{
                          paddingHorizontal: 12,
                          paddingVertical: 6,
                          borderRadius: 8,
                          backgroundColor: selectedDatabaseConnection === conn._id
                            ? (theme ? "#3b82f6" : "#2563eb")
                            : (theme ? "#e2e8f0" : "#334155"),
                          borderWidth: 1,
                          borderColor: selectedDatabaseConnection === conn._id
                            ? (theme ? "#2563eb" : "#1d4ed8")
                            : (theme ? "#cbd5e1" : "#475569")
                        }}
                      >
                        <Text style={{
                          color: selectedDatabaseConnection === conn._id
                            ? "white"
                            : (theme ? "#64748b" : "#94a3b8"),
                          fontSize: 12,
                          fontWeight: '600'
                        }}>
                          {conn.name} ({conn.type})
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                </View>
              )}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
                {[
                  {
                    title: 'Data Collections',
                    value: totalDataItems.toString(),
                    icon: 'folder',
                    color: theme ? "#0ea5e9" : "#38bdf8",
                    bgColor: theme ? "#dbeafe" : "#0c4a6e"
                  },
                  {
                    title: 'Total Records',
                    value: totalRecords.toString(),
                    icon: 'description',
                    color: theme ? "#059669" : "#10b981",
                    bgColor: theme ? "#d1fae5" : "#064e3b"
                  },
                  {
                    title: 'Active Filter',
                    value: activeFilter ? activeFilter.value : 'All',
                    icon: 'filter-list',
                    color: theme ? "#7c3aed" : "#a78bfa",
                    bgColor: theme ? "#ede9fe" : "#312e81"
                  },
                  {
                    title: 'Data Type',
                    value: dataFilter === 'documents' ? 'Documents' :
                      dataFilter === 'invoices' ? 'Invoices' : 'Mixed',
                    icon: 'category',
                    color: theme ? "#f59e0b" : "#fbbf24",
                    bgColor: theme ? "#fef3c7" : "#451a03"
                  }
                ].map((stat, index) => (
                  <Animatable.View
                    key={index}
                    animation="bounceIn"
                    delay={index * 200}
                    style={[
                      styles.modernStatsCard,
                      {
                        flexBasis: Platform.OS === 'web' ? '22%' : '45%',
                        flexGrow: 1,
                        minWidth: "22%",
                        maxWidth: Platform.OS === 'web' ? 180 : undefined,
                        backgroundColor: stat.bgColor,
                        borderRadius: 12,
                        padding: 16,
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderWidth: 1,
                        borderColor: theme ? '#e2e8f0' : '#374151'
                      }
                    ]}
                  >
                    <MaterialIcons
                      name={stat.icon as any}
                      size={24}
                      color={stat.color}
                      style={{ marginBottom: 8 }}
                    />
                    <Text style={{
                      color: stat.color,
                      fontSize: 20,
                      fontWeight: '700',
                      marginBottom: 4
                    }}>
                      {stat.value}
                    </Text>
                    <Text style={{
                      color: theme ? "#64748b" : "#94a3b8",
                      fontSize: 12,
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {toTitleCase(stat.title)}
                    </Text>
                  </Animatable.View>
                ))}
              </View>
            </YStack>
          </XStack>
        )}
      </Animatable.View>
    )
  }, [processedData, paginatedData, dataFilter, theme, isHeaderCollapsed, quickAddRef, searchRef, linkGenRef]);


  return (
    <DefaultScreen
      layoutCallback={({ width }) => {
        setWidth(width);
      }}
      scrollDisabled // Keep scrollDisabled as Virtuoso manages its own scroll
      screenTitle={'Data Store'}
      containerStyle={{ flex: 1, paddingBottom: 40, width: '100%', overflow: 'hidden' }}
      searchBar={{
        callback: setSearchState,
        searchState: searchState,
        customIcons: [
          { function: () => robotRef.current?.showHelp(), icon: <Entypo name="help" size={18} color={"white"} />, functionName: 'help' },
          { function: refetch, icon: <FontAwesome name="refresh" size={18} color={"white"} />, functionName: 'refresh' },
          { function: handleCreateAction, icon: <MaterialIcons name="create-new-folder" size={18} color="white" />, functionName: 'createData' }
        ]
      }}
    >
      {/* Modals */}
      <QuickAddDataModal ref={quickAddRef} />
      <QuickSearchModal ref={searchRef} />
      <LinkGenModal idOnly url={`${HOST}/forms/builder`} ref={linkGenRef} />
      <DataOptionsModal ref={dataOptionsRef} title={(activeItem as any)?.dataTitle || ""} refreshFunction={refetch} />
      <ActionsModal ref={actionsRef} />
      <DataDeleteConfirmModal ref={deleteConfirmRef} deleteFunction={apis.deleteData} refreshFunction={refetch} />
      <ShareUserModal ref={shareUserRef} item={activeItem} shareFunction={apis.shareDataItem} />
      
      {(isLoading || sentInvoicesLoading || menuResizing) ? (
        <View style={{ flex: 1, alignItems: 'center', justifyContent: "center" }}>
          <GlobalTutorial customHeight={400} showArrows customWidth={350} carouselData={newCarouselData} />
        </View>
      ) : (
          <View onLayout={handleContainerLayout} style={{ flex: 1, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
            {(
              <FlatList
                key={`data-${columns}-${listHorizontal ? 'horizontal' : 'vertical'}`}
                style={{ flex: 1, alignSelf: "stretch" }}
                  scrollEnabled={processedData.length > 0}
                  showsVerticalScrollIndicator={false}
                  data={processedData}
                  numColumns={columns}
                directionalLockEnabled
                  keyExtractor={(item, index) => item._id || `item-${index}`}
                  renderItem={renderVirtuosoItem}
                  ListHeaderComponent={<HeaderComponent />}
                contentContainerStyle={{ alignSelf: 'stretch', minWidth: '100%', paddingBottom: 150, gap: 20 }}
                  removeClippedSubviews={false}
                nestedScrollEnabled={true}
              />
            )}
            {
              !processedData?.length && !searchState && (
                <ListEmptyComponent />
              )
            }

            {/* Integrated Pagination and Sorting Controls */}
            {paginationState && (
              <Pagination
                pagination={dataFilter === 'invoices'
                  ? (sentInvoicesData?.data?.pagination ? {
                    currentPage: sentInvoicesData.data.pagination.currentPage || 1,
                    totalPages: sentInvoicesData.data.pagination.totalPages || 1,
                    totalRecords: sentInvoicesData.data.pagination.totalRecords || 0,
                    hasNextPage: sentInvoicesData.data.pagination.hasNextPage || false,
                    hasPrevPage: sentInvoicesData.data.pagination.hasPrevPage || false,
                    nextPage: sentInvoicesData.data.pagination.nextPage,
                    prevPage: sentInvoicesData.data.pagination.prevPage
                  } : {
                    currentPage: 1,
                    totalPages: 1,
                    totalRecords: processedData?.length || 0,
                    hasNextPage: false,
                    hasPrevPage: false,
                    nextPage: undefined,
                    prevPage: undefined
                  })
                  : (paginatedData?.data?.pagination ? {
                    currentPage: paginatedData.data.pagination.currentPage || 1,
                    totalPages: paginatedData.data.pagination.totalPages || 1,
                    totalRecords: paginatedData.data.pagination.totalRecords || 0,
                    hasNextPage: paginatedData.data.pagination.hasNextPage || false,
                    hasPrevPage: paginatedData.data.pagination.hasPrevPage || false,
                    nextPage: paginatedData.data.pagination.nextPage,
                    prevPage: paginatedData.data.pagination.prevPage
                  } : {
                    currentPage: 1,
                    totalPages: 1,
                    totalRecords: processedData?.length || 0,
                    hasNextPage: false,
                    hasPrevPage: false,
                    nextPage: undefined,
                    prevPage: undefined
                  })
                }
                pageSize={paginationState.pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[6, 12, 24, 48]}
                showPageSizeSelector={true}
                style={{
                  bottom: 40
                }}
                showTotalRecords={true}
                showSorting={true}
                sortBy={paginationState.sortBy}
                sortOrder={paginationState.sortOrder}
                onSortByChange={handleSortByChange}
                onSortOrderChange={handleSortOrderChange}
                sortByOptions={dataFilter === 'invoices'
                  ? [
                    { value: 'issue_date', label: 'Issue Date' },
                    { value: 'due_date', label: 'Due Date' },
                    { value: 'invoice_number', label: 'Invoice Number' },
                    { value: 'total', label: 'Total Amount' }
                  ]
                  : [
                    { value: 'createdAt', label: 'Created Date' },
                    { value: 'updatedAt', label: 'Updated Date' },
                    { value: 'dataTitle', label: 'Title' },
                    { value: 'name', label: 'Name' }
                  ]
                }
              />
            )}
            </View>
      )}
      
    </DefaultScreen>
  );
};



export default CaptureScreen;
